import type { PlanDelCatalogo } from "@/schemas/billing";

/**
 * Cuál de los planes del catálogo es el que tiene el negocio.
 *
 * # Por qué se deduce acá y no viene dado
 *
 * Porque todavía no hay de dónde: el borde no expone el plan vigente
 * (trd-bff#272 lo va a hacer, y ahí esta función se borra — ver trd-client#239
 * §3, "el plan actual tiene que venir del backend"). Hasta entonces se deduce
 * del entitlement: un plan es "el tuyo" si todas sus features están habilitadas
 * en tus `modules`.
 *
 * # Dos trampas de la deducción anterior
 *
 * 1. **Un plan sin features matcheaba siempre.** `[].every()` es `true`, así que
 *    un plan vacío del catálogo se llevaba puesto al plan real. Acá se exige al
 *    menos una feature para considerarlo.
 * 2. **Se quedaba con el primero que matcheara.** Los planes son anidados
 *    (starter ⊆ professional ⊆ enterprise), así que un negocio enterprise
 *    matchea los tres y mostraba *starter*. Acá gana el más específico: el que
 *    tiene MÁS features, que es el más chico que te cubre.
 *
 * Devuelve `null` cuando no se puede decidir. `null` significa **no sé**, y la
 * UI tiene que decirlo — nunca traducirlo a "plan gratuito".
 */
export function encontrarPlanActual(
  planes: PlanDelCatalogo[] | undefined | null,
  modules: string[] | undefined | null
): PlanDelCatalogo | null {
  if (!planes?.length) return null;
  const habilitados = new Set(modules ?? []);
  if (habilitados.size === 0) return null;

  const candidatos = planes.filter((plan) => {
    // `module_set` es el `features[].module` del contrato viejo: la lista de
    // módulos que ese plan habilita.
    const modulos = plan.module_set ?? [];
    // Un plan sin módulos no dice nada: no puede ganar por vacío.
    if (modulos.length === 0) return false;
    return modulos.every((m) => habilitados.has(m));
  });

  if (candidatos.length === 0) return null;

  return candidatos.reduce((mejor, plan) =>
    (plan.module_set?.length ?? 0) > (mejor.module_set?.length ?? 0)
      ? plan
      : mejor
  );
}

/**
 * ¿Es el plan más caro de los que se ofrecen?
 *
 * Reemplaza al `tier === "enterprise"` que decidía entre "Ver plan" y "Mejorar
 * plan": **el contrato nuevo no tiene `tier`**. Y es más exacto — el tier era una
 * etiqueta, esto es el dato: si ya estás en el más caro, no hay a qué mejorar.
 *
 * El precio se compara como **plata**, en centavos enteros, sin pasar por float.
 */
export function esElPlanMasCaro(
  planes: PlanDelCatalogo[],
  planId: string | null | undefined
): boolean {
  if (!planId || planes.length === 0) return false;
  const centavos = (p: PlanDelCatalogo) => {
    if (p.is_free || !p.price) return 0;
    const [entero, dec = ""] = p.price.trim().split(".");
    return Number(entero) * 100 + Number(dec.padEnd(2, "0").slice(0, 2) || 0);
  };
  const tope = Math.max(...planes.map(centavos));
  const plan = planes.find((p) => p.plan_id === planId);
  return !!plan && centavos(plan) === tope;
}
